import React, { useState, useEffect, useMemo } from 'react'
import {
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  Paper,
  Box,
  Chip,
} from '@material-ui/core'
import { Search, Close } from '@material-ui/icons'
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles'
import { connect } from 'react-redux'
import { getAttributeValues } from '@/redux/actions/taxons'
import { GetMapDispatchTypes } from '@/interfaces/common/redux'
import { enableSearchFilterValuesFor } from '@/utils/constants'
import { useRouter } from 'next/router'
import { useTranslation } from 'react-i18next'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      position: 'relative',
      width: '100%',
    },
    input: {
      width: '100%',
      '& .MuiInputBase-root': {
        borderRadius: 4,
      },
    },
    dropdown: {
      position: 'absolute',
      top: '100%',
      left: 0,
      right: 0,
      zIndex: 1000,
      maxHeight: 210,
      overflowY: 'auto',
      backgroundColor: theme.palette.common.white,
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 4,
      boxShadow: theme.shadows[2],
    },
    listItem: {
      padding: theme.spacing(0.5, 2),
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      },
    },
    selectedValues: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: theme.spacing(0.5),
      marginTop: theme.spacing(1),
    },
    chip: {
      margin: theme.spacing(0.25),
    },
    noResults: {
      padding: theme.spacing(2),
      textAlign: 'center',
      color: theme.palette.text.secondary,
    },
  })
)

interface AttributeValue {
  key: string
  value: string
}

type Tprops = {
  attributeCode: string
  attributeName: string
  selectedValues: string[]
  onValuesChange: (values: string[]) => void
  attributeValues?: AttributeValue[]
  loading?: boolean
} & GetMapDispatchTypes<typeof mapDispatchToProps>

const mapDispatchToProps = {
  getAttributeValues,
}

const AutocompleteFilter = ({
  attributeCode,
  attributeName,
  selectedValues,
  onValuesChange,
  attributeValues = [],
  loading = false,
  getAttributeValues,
}: Tprops) => {
  const classes = useStyles()
  const router = useRouter()
  const { t } = useTranslation()
  const locale = router.locale || 'fr'
  const [searchQuery, setSearchQuery] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [inputRef, setInputRef] = useState<HTMLDivElement | null>(null)
  const [displayedCount, setDisplayedCount] = useState(10)
  const [isLoadingMore, setIsLoadingMore] = useState(false)

  // Fetch attribute values when component mounts or locale changes
  useEffect(() => {
    if (enableSearchFilterValuesFor.includes(attributeCode)) {
      getAttributeValues(attributeCode)
    }
  }, [attributeCode, getAttributeValues, locale])

  // Clear search when locale changes
  useEffect(() => {
    setSearchQuery('')
    setDisplayedCount(10)
  }, [locale])

  // Reset displayed count when search changes
  useEffect(() => {
    setDisplayedCount(10)
  }, [searchQuery])

  // Filter values based on search query
  const filteredValues = useMemo(() => {
    let filtered = attributeValues
    if (searchQuery.trim()) {
      filtered = attributeValues.filter((value) =>
        value.value.toLowerCase().includes(searchQuery.toLowerCase())
      )
    }
    return filtered.slice(0, displayedCount)
  }, [attributeValues, searchQuery, displayedCount])

  // Check if there are more values to load
  const hasMoreValues = useMemo(() => {
    let filtered = attributeValues
    if (searchQuery.trim()) {
      filtered = attributeValues.filter((value) =>
        value.value.toLowerCase().includes(searchQuery.toLowerCase())
      )
    }
    return filtered.length > displayedCount
  }, [attributeValues, searchQuery, displayedCount])

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value)
    setDisplayedCount(10) // Reset to first 10 when searching
    setIsOpen(true)
  }

  const handleLoadMore = (event: React.MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setIsLoadingMore(true)
    // Simulate loading delay
    setTimeout(() => {
      setDisplayedCount((prev) => prev + 10)
      setIsLoadingMore(false)
    }, 300)
  }

  const handleValueToggle = (valueKey: string, event?: React.MouseEvent) => {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }
    const newValues = selectedValues.includes(valueKey)
      ? selectedValues.filter((key) => key !== valueKey)
      : [...selectedValues, valueKey]
    onValuesChange(newValues)
  }

  const handleRemoveValue = (valueKey: string) => {
    const newValues = selectedValues.filter((key) => key !== valueKey)
    onValuesChange(newValues)
  }

  const handleInputFocus = () => {
    setIsOpen(true)
  }

  const handleInputBlur = (event: React.FocusEvent) => {
    // Delay closing to allow clicking on dropdown items
    setTimeout(() => {
      if (!inputRef?.contains(event.relatedTarget as Node)) {
        setIsOpen(false)
      }
    }, 150)
  }

  return (
    <div className={classes.container}>
      <TextField
        ref={setInputRef}
        className={classes.input}
        placeholder={t('texts:shop:autocomplete_search_placeholder', {
          attributeName,
        })}
        value={searchQuery}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        InputProps={{
          startAdornment: <Search color="action" />,
        }}
        disabled={loading}
      />

      {isOpen && (
        <Paper className={classes.dropdown}>
          {loading ? (
            <div className={classes.noResults}>
              {t('texts:shop:autocomplete_loading')}
            </div>
          ) : filteredValues.length > 0 ? (
            <List dense>
              {filteredValues.map((value) => (
                <ListItem
                  key={value.key}
                  className={classes.listItem}
                  onClick={(e) => handleValueToggle(value.key, e)}
                  onMouseDown={(e) => e.preventDefault()}
                  button
                >
                  <ListItemIcon>
                    <Checkbox
                      checked={selectedValues.includes(value.key)}
                      edge="start"
                      disableRipple
                    />
                  </ListItemIcon>
                  <ListItemText primary={value.value} />
                </ListItem>
              ))}
            </List>
          ) : (
            <div className={classes.noResults}>
              {searchQuery
                ? t('texts:shop:autocomplete_no_results')
                : t('texts:shop:autocomplete_no_values')}
            </div>
          )}
          {hasMoreValues && (
            <div
              style={{
                padding: '8px',
                textAlign: 'center',
                borderTop: '1px solid #e0e0e0',
              }}
            >
              <button
                onClick={handleLoadMore}
                onMouseDown={(e) => e.preventDefault()}
                disabled={isLoadingMore}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#1976d2',
                  cursor: 'pointer',
                  padding: '8px 16px',
                  fontSize: '14px',
                }}
              >
                {isLoadingMore
                  ? t('texts:shop:autocomplete_loading')
                  : t('texts:shop:autocomplete_load_more')}
              </button>
            </div>
          )}
        </Paper>
      )}

      {selectedValues.length > 0 && (
        <Box className={classes.selectedValues}>
          {selectedValues.map((key) => {
            const value = attributeValues.find((v) => v.key === key)
            return (
              <Chip
                key={key}
                label={value?.value || key}
                onDelete={() => handleRemoveValue(key)}
                className={classes.chip}
                size="small"
                deleteIcon={<Close />}
              />
            )
          })}
        </Box>
      )}
    </div>
  )
}

export default connect(null, mapDispatchToProps)(AutocompleteFilter)
